import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { Search, FolderOpen, ArrowLeft, Lock, Unlock, Database, Folder, Clock, Zap, RotateCw } from 'lucide-react';

// Add CSS animation for spinner
const spinKeyframes = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;
import { addNotification } from '../../store/slices/uiSlice';
import { setExtractionResults } from '../../store/slices/documentsSlice';
import ExportButton from '../ExportButton';
import FilterButton, { FilterButtonGroup } from '../FilterButton';
import MultiSelectTypeahead from '../MultiSelectTypeahead';
import PanelButton from '../PanelButton';
import { X } from 'lucide-react';
import { apiFetch } from '../../utils/apiUtils';

const SummaryMetrics = styled.div`
  display: flex;
  gap: 0.25rem;
  margin-bottom: 1.5rem;
`;

const MetricCard = styled.div`
  flex: 1;
  text-align: center;
  padding: 0.375rem;
  background: #3a3a3a;
  border-radius: 4px;
`;

const MetricValue = styled.div`
  font-size: 0.95rem;
  font-weight: 700;
  color: white;
  margin-bottom: 0.125rem;
`;

const MetricLabel = styled.div`
  font-size: 0.6rem;
  color: white;
  text-transform: uppercase;
  letter-spacing: 0.2px;
  white-space: nowrap;
`;

const Divider = styled.div`
  height: 1px;
  background: #666;
  margin: 1.5rem 0 1rem 0;
  opacity: 0.8;
`;

const SearchContainer = styled.div`
  position: relative;
  margin-bottom: 1rem;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid #555;
  border-radius: 6px;
  font-size: 0.875rem;
  background: #2d2d2d;
  color: #f0f0f0;
  
  &::placeholder {
    color: #a0a0a0;
  }
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #a0a0a0;
  display: flex;
  align-items: center;
`;

const SectionDivider = styled.div`
  height: 1px;
  background: #666;
  margin: 1.5rem 0;
  opacity: 0.8;
`;

const SectionTitle = styled.h3`
  font-size: 0.875rem;
  font-weight: 600;
  color: #f0f0f0;
  margin: 0 0 0.75rem 0;
`;

const QuickActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.75rem;
`;

const ActionButton = styled.button`
  width: 100%;
  padding: 0.75rem 1rem;
  background: #4a4a4a;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: #e0e0e0;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  
  &:hover {
    background: #4b5563;
    border-color: rgba(255, 255, 255, 0.2);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

function SummaryContent({
  isCreateMode,
  isDocumentAnalysis,
  hasResults,
  showCaseStatusPanel,
  showScansPanel,
  setShowScansPanel,
  onNavigateToScans,
  onNavigateToSummary,
  onReRunExtraction,
  loanId,
  renderCaseStatusContent,
  renderScansContent,
  renderInstructionsContent,
  metrics,
  searchQuery,
  setSearchQuery,
  filters,
  activeFilter,
  setActiveFilter,
  caseStatus,
  setSelectedStatus,
  setShowCaseStatusPanel,
  exportToCSV,
  location,
  latestRecords,
  selectedContracts,
  setSelectedContracts,
  extractionResults,
  handleSaveToDatabase,
  databaseLoading
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // Get the correct loading state from documents slice
  const documentsLoading = useSelector(state => state.documents?.loading || {});
  
  // Inject CSS animation for spinner
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = spinKeyframes;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);
  
  const handleReRunDataMatching = async () => {
    if (!loanId) {
      dispatch(addNotification({
        type: 'error',
        message: 'No loan ID available for re-running data matching',
        duration: 3000
      }));
      return;
    }
    
    try {
      // Show starting notification
      dispatch(addNotification({
        type: 'info',
        message: '🔄 Starting re-extraction...',
        duration: 3000
      }));
      
      const response = await apiFetch(`/documents/re-extract/${loanId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to re-run data matching');
      }
      
      const result = await response.json();
      
      console.log('Re-extraction result:', result);
      
      // Parse the extraction results and store them for application after progress completes
      if (result.success && result.data) {
        const extractionResults = JSON.parse(result.data);
        
        console.log('Parsed extraction results:', extractionResults);
        console.log('Number of fields:', Object.keys(extractionResults).length);
        
        // Store results in sessionStorage to be applied when progress completes
        // The DocumentAnalysis page's useProgress callback will apply them
        sessionStorage.setItem('pendingExtractionResults', JSON.stringify(extractionResults));
        
      }
      
    } catch (error) {
      console.error('Error re-running data matching:', error);
      dispatch(addNotification({
        type: 'error',
        message: `❌ Failed to re-run data matching: ${error.message}`,
        duration: 5000
      }));
    }
  };
  
  const handleReRunExtractionWithPreview = () => {
    // Open the extraction preview modal
    if (typeof onReRunExtraction === 'function') {
      onReRunExtraction();
    } else {
      console.error('Re-run extraction callback is not available');
    }
  };
  
  if (isCreateMode) {
    return renderInstructionsContent();
  }
  
  // Show instructions when in DocumentAnalysis mode but no results yet
  if (isDocumentAnalysis && !hasResults) {
    return renderInstructionsContent();
  }
  
  // If no loan is selected, show "Not Implemented" message
  if (!loanId) {
    return (
      <div style={{
        padding: '2rem',
        textAlign: 'center',
        color: '#9ca3af',
        fontSize: '0.875rem'
      }}>
        <div style={{ marginBottom: '0.5rem', fontSize: '1rem', fontWeight: '500' }}>
          Not Implemented
        </div>
        <div>
          This section is only available when a loan has been selected.
        </div>
      </div>
    );
  }
  
  // For Document Analysis pages, show the full functionality
  if (isDocumentAnalysis) {
    // If scans panel is open, show scans content
    if (showScansPanel) {
      return (
        <div>
          <div style={{ marginBottom: '1rem' }}>
            <PanelButton onClick={onNavigateToSummary}>
              <ArrowLeft size={16} />
              Back to Summary
            </PanelButton>
          </div>
          {renderScansContent && renderScansContent()}
        </div>
      );
    }
    
    // If case status panel is open, only show that content
    if (showCaseStatusPanel) {
      return renderCaseStatusContent();
    }
    
    // Otherwise show the full summary content
    return (
      <>

        <SearchContainer>
          <SearchIcon>
            <Search size={16} />
          </SearchIcon>
          <SearchInput
            type="text"
            placeholder="Type anything..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
            }}
            data-cy="grid-search"
          />
        </SearchContainer>


        <SectionDivider />

        <SectionTitle>Case Status</SectionTitle>
        <div style={{ marginBottom: '1rem' }}>
          <button
            onClick={() => {
              setSelectedStatus(caseStatus);
              setShowCaseStatusPanel(true);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1rem',
              background: '#3a3a3a',
              border: '1px solid #4a4a4a',
              borderRadius: '4px',
              color: '#e0e0e0',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '500',
              transition: 'all 0.2s',
              width: '100%',
              textAlign: 'left'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = '#4a4a4a';
              e.target.style.borderColor = '#6a6a6a';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = '#3a3a3a';
              e.target.style.borderColor = '#4a4a4a';
            }}
          >
            {(() => {
                  const statusOptions = [
                    { key: 'open', label: 'Open', icon: FolderOpen, color: '#3b82f6' },
                    { key: 'under_review', label: 'Under Review', icon: Clock, color: '#f59e0b' },
                    { key: 'closed', label: 'Closed', icon: Folder, color: '#6b7280' }
                  ];
              const currentStatus = statusOptions.find(s => s.key === caseStatus);
              const StatusIcon = currentStatus?.icon || FolderOpen;
              return (
                <>
                  <StatusIcon 
                    size={16} 
                    color={currentStatus?.color || '#3b82f6'}
                    style={{ flexShrink: 0 }}
                  />
                  <span>Current: {currentStatus?.label || 'Open'}</span>
                  <span style={{ marginLeft: 'auto', fontSize: '0.75rem', opacity: 0.7 }}>
                    Click to change
                  </span>
                </>
              );
            })()}
          </button>
        </div>

        <SectionDivider />

        <SectionTitle>Documents</SectionTitle>
        <div style={{ marginBottom: '1rem' }}>
          <PanelButton onClick={() => navigate(`/document-analysis/${loanId}/documents`)}>
            <FolderOpen size={16} />
            View Documents
          </PanelButton>
        </div>
        {/* Temporarily commented out - can be added back later */}
        {/* <div style={{ marginBottom: '1rem' }}>
          <PanelButton onClick={handleReRunDataMatching}>
            <RotateCw size={16} />
            Re-run Data Matching
          </PanelButton>
        </div> */}
        <div style={{ marginBottom: '1rem' }}>
          <PanelButton onClick={handleReRunExtractionWithPreview} data-cy="rerun-extraction-button">
            <Search size={16} />
            Re-run Extraction (Preview)
          </PanelButton>
        </div>

        <SectionDivider />

        <SectionTitle>Quick Actions</SectionTitle>
        <QuickActions>
          <ExportButton onClick={exportToCSV}>Export CSV</ExportButton>
          <ExportButton>Export PDF</ExportButton>
        </QuickActions>

        {/* Save to Database button - only show if we have extraction results */}
        {hasResults && extractionResults && Object.keys(extractionResults).length > 0 && (
          <>
            <SectionDivider />
            <SectionTitle>Database</SectionTitle>
            <PanelButton 
              onClick={handleSaveToDatabase}
              disabled={documentsLoading?.save}
            >
              <Database size={16} />
              {documentsLoading?.save ? (
                <>
                  <RotateCw size={16} style={{ animation: 'spin 1s linear infinite', marginRight: '8px' }} />
                  Saving...
                </>
              ) : 'Save to Database'}
            </PanelButton>
          </>
        )}

        <div style={{ fontSize: '0.875rem', color: '#a0a0a0', marginTop: '1rem' }}>
          {filters.find(f => f.key === activeFilter)?.count || metrics.totalFields} total results
        </div>
      </>
    );
  }
  
  // For other pages, show system summary data
  return (
    <>
      {/* Loan Filtering Section */}
            
            {/* Multi-Select Search Input */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ 
                display: 'block', 
                color: '#a0a0a0', 
                fontSize: '0.75rem', 
                marginBottom: '0.5rem',
                fontWeight: '500'
              }}>
                Search & Select Loans
              </label>
              
              {location.pathname.includes('/system-files') && latestRecords.length > 0 ? (
                <div>
                  <MultiSelectTypeahead
                    items={latestRecords}
                    selectedItems={selectedContracts}
                    onSelectionChange={setSelectedContracts}
                    placeholder="Search by loan ID, borrower, amount..."
                    getItemLabel={(contract) => `${contract.borrower_name || 'Unknown'} - ${contract.loan_id}`}
                    getItemValue={(contract) => contract.loan_id}
                    maxVisibleTags={0} // Don't show tags in the component
                    isDark={true}
                  />
                  
                  {/* Selected Tags - Show directly below search */}
                  {selectedContracts.length > 0 && (
                    <div style={{ 
                      display: 'flex', 
                      flexWrap: 'wrap', 
                      gap: '0.5rem', 
                      marginTop: '0.75rem' 
                    }}>
                      {selectedContracts.slice(0, 2).map((contract) => (
                        <div
                          key={contract?.loan_id || Math.random()}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            padding: '0.25rem 0.5rem',
                            background: '#4a4a4a',
                            border: '1px solid #555',
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            color: '#e0e0e0'
                          }}
                        >
                          <span>{contract?.borrower_name || 'Unknown'} - {contract?.loan_id || 'No ID'}</span>
                          <button
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#a0a0a0',
                              cursor: 'pointer',
                              padding: 0,
                              display: 'flex',
                              alignItems: 'center'
                            }}
                            onClick={() => {
                              setSelectedContracts(selectedContracts.filter(c => c?.loan_id !== contract?.loan_id));
                            }}
                          >
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                      {selectedContracts.length > 2 && (
                        <div style={{ 
                          padding: '0.25rem 0.5rem',
                          background: '#3a3a3a',
                          color: '#a0a0a0',
                          border: '1px solid #555',
                          borderRadius: '4px',
                          fontSize: '0.75rem'
                        }}>
                          + {selectedContracts.length - 2} more
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <input
                  type="text"
                  placeholder="Search by loan ID, borrower, amount..."
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #555',
                    borderRadius: '4px',
                    background: '#2d2d2d',
                    color: '#f0f0f0',
                    fontSize: '0.8rem',
                    outline: 'none'
                  }}
                />
              )}
              
              {/* Bulk Export Button - Show when contracts selected */}
              {selectedContracts.length > 0 && (
                <div style={{ marginTop: '0.75rem' }}>
                  <ExportButton 
                    onClick={() => {
                      alert(`Exporting ${selectedContracts.length} contracts... (Feature coming soon)`);
                    }}
                    style={{ width: '100%', fontSize: '0.75rem', padding: '0.5rem' }}
                  >
                    Export {selectedContracts.length} Selected
                  </ExportButton>
                </div>
              )}
            </div>

            {/* Field Status Filters */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ 
                display: 'block', 
                color: '#a0a0a0', 
                fontSize: '0.75rem', 
                marginBottom: '0.5rem',
                fontWeight: '500'
              }}>
                Field Status
              </label>
              <FilterButtonGroup>
                <FilterButton isActive={true}>
                  All Fields
                </FilterButton>
                <FilterButton>
                  High Match
                </FilterButton>
                <FilterButton>
                  Low Match
                </FilterButton>
              </FilterButtonGroup>
            </div>

            {/* Missing Fields Filter */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ 
                display: 'block', 
                color: '#a0a0a0', 
                fontSize: '0.75rem', 
                marginBottom: '0.5rem',
                fontWeight: '500'
              }}>
                Missing Fields
              </label>
              <select style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #555',
                borderRadius: '4px',
                background: '#2d2d2d',
                color: '#f0f0f0',
                fontSize: '0.8rem',
                outline: 'none'
              }}>
                <option value="">Show loans missing...</option>
                <option value="borrower_name">Borrower Name</option>
                <option value="loan_amount">Loan Amount</option>
                <option value="contract_date">Contract Date</option>
                <option value="maturity_date">Maturity Date</option>
                <option value="interest_rate">Interest Rate</option>
                <option value="ltv_ratio">LTV Ratio</option>
              </select>
            </div>

            {/* Filled Fields Filter */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ 
                display: 'block', 
                color: '#a0a0a0', 
                fontSize: '0.75rem', 
                marginBottom: '0.5rem',
                fontWeight: '500'
              }}>
                Required Fields
              </label>
              <select style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #555',
                borderRadius: '4px',
                background: '#2d2d2d',
                color: '#f0f0f0',
                fontSize: '0.8rem',
                outline: 'none'
              }}>
                <option value="">Show loans with...</option>
                <option value="borrower_name">Borrower Name</option>
                <option value="loan_amount">Loan Amount</option>
                <option value="contract_date">Contract Date</option>
                <option value="maturity_date">Maturity Date</option>
                <option value="interest_rate">Interest Rate</option>
                <option value="ltv_ratio">LTV Ratio</option>
              </select>
            </div>

            {/* Data Sources Filter */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ 
                display: 'block', 
                color: '#a0a0a0', 
                fontSize: '0.75rem', 
                marginBottom: '0.5rem',
                fontWeight: '500'
              }}>
                Data Sources
              </label>
              <select style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #555',
                borderRadius: '4px',
                background: '#2d2d2d',
                color: '#f0f0f0',
                fontSize: '0.8rem',
                outline: 'none'
              }}>
                <option value="">All Sources</option>
                <option value="1">Single Source</option>
                <option value="2">Multiple Sources</option>
                <option value="3">3+ Sources</option>
              </select>
            </div>

            {/* Date Range Filter */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ 
                display: 'block', 
                color: '#a0a0a0', 
                fontSize: '0.75rem', 
                marginBottom: '0.5rem',
                fontWeight: '500'
              }}>
                Last Updated
              </label>
              <select style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #555',
                borderRadius: '4px',
                background: '#2d2d2d',
                color: '#f0f0f0',
                fontSize: '0.8rem',
                outline: 'none'
              }}>
                <option value="">Any Time</option>
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
                <option value="quarter">This Quarter</option>
              </select>
            </div>

      {/* Clear Filters Button */}
      <ActionButton style={{ marginBottom: '1rem' }}>
        Clear All Filters
      </ActionButton>

      {/* Export CSV Button */}
      <ExportButton onClick={exportToCSV}>
        Export CSV
      </ExportButton>
    </>
  );
}

export default SummaryContent;
